import { ReactNode } from "react";
import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import { CloseRounded } from "@mui/icons-material";

import WizardStepper from "./WizardStepper";

// ----------------------------------------------------------------------

interface WizardShellProps {
  steps: string[];
  activeIndex: number;
  children: ReactNode;
  /** Passo 1: cancelar (texto). Passos 2+: voltar (ghost). Forneça só o aplicável */
  onCancel?: () => void;
  onBack?: () => void;
  backLabel?: string;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  /** Botões adicionais entre Voltar e o principal (ex.: Pré-visualizar no Dose) */
  footerExtra?: ReactNode;
}

/** Casca de passo do wizard: stepper + card + footer responsivo */
export default function WizardShell({
  steps,
  activeIndex,
  children,
  onCancel,
  onBack,
  backLabel = "Voltar",
  onNext,
  nextLabel = "Continuar",
  nextDisabled = false,
  footerExtra,
}: WizardShellProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const cancelBtn = onCancel && (
    <Button
      onClick={onCancel}
      startIcon={<CloseRounded sx={{ fontSize: 16 }} />}
      sx={{
        height: isMobile ? 50 : 48,
        px: 2.5,
        border: `1.5px solid ${theme.palette.divider}`,
        color: "text.secondary",
        backgroundColor: "background.paper",
        "&:hover": { backgroundColor: theme.palette.brand.surface2 },
        ...(isMobile && { flex: 1 }),
      }}
    >
      Cancelar
    </Button>
  );
  const backBtn = onBack && (
    <Button onClick={onBack} sx={{ height: isMobile ? 50 : 48, px: 2.5, border: `1.5px solid ${theme.palette.divider}`, color: "secondary.main", backgroundColor: "background.paper", ...(isMobile && { flex: 1 }) }}>
      {backLabel}
    </Button>
  );
  const nextBtn = onNext && (
    // No mobile todos os botões do rodapé dividem a largura igualmente, para as
    // duas linhas ficarem alinhadas; o principal já se destaca pelo preenchimento.
    <Button variant="contained" onClick={onNext} disabled={nextDisabled} sx={{ height: isMobile ? 50 : 48, px: isMobile ? 2.5 : 3, ...(isMobile && { flex: 1 }) }}>
      {nextLabel}
    </Button>
  );

  const footer = isMobile ? (
    // Mobile: footer fixo. Botões extras (ex.: Pré-visualizar/Gerar receita na Dose)
    // vão numa linha de cima full-width; Voltar/Salvar na linha de baixo.
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: theme.zIndex.appBar,
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        p: "12px 16px calc(16px + env(safe-area-inset-bottom))",
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: "background.paper",
      }}
    >
      {footerExtra && <Box sx={{ display: "flex", gap: "10px", "& > *": { flex: 1, m: 0 } }}>{footerExtra}</Box>}
      <Box sx={{ display: "flex", gap: "10px" }}>
        {cancelBtn}
        {backBtn}
        {nextBtn}
      </Box>
    </Box>
  ) : (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: "22px", maxWidth: 880, mx: "auto", width: "100%" }}>
      {cancelBtn}
      {backBtn}
      <Box sx={{ flex: 1 }} />
      {footerExtra}
      {nextBtn}
    </Box>
  );

  return (
    <Box sx={{ width: "100%", flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: { xs: "14px 16px 96px", md: 4 } }}>
      <WizardStepper steps={steps} activeIndex={activeIndex} />
      <Box
        sx={{
          maxWidth: 880,
          width: "100%",
          mx: "auto",
          flex: 1,
          minHeight: 400,
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.paper",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "18px",
          p: { xs: "18px 16px", md: "30px 32px" },
        }}
      >
        {/* Área de conteúdo: preenche o card e rola por dentro, mantendo
            stepper/footer fixos e o card sempre do mesmo tamanho. */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            pt: 1,
            mr: -1,
            pr: 1,
            "&::-webkit-scrollbar": { width: 4 },
            "&::-webkit-scrollbar-thumb": { borderRadius: 2, backgroundColor: theme.palette.divider },
          }}
        >
          {children}
        </Box>
      </Box>
      {footer}
    </Box>
  );
}
